/**
 * Dashboard Widget Registry
 *
 * Central registry of all available dashboard widgets.
 * Each widget has metadata for the Dashboard Builder UI.
 */

import { ClientFundsGridWidget } from './widgets/ClientFundsGridWidget'
import { AdvisorSummaryWidget } from './widgets/AdvisorSummaryWidget'
import { FundSummaryWidget } from './widgets/FundSummaryWidget'
import { ActivityTableWidget } from './widgets/ActivityTableWidget'
import { QuickActionsWidget } from './widgets/QuickActionsWidget'
import { GivingWalletWidget } from './widgets/GivingWalletWidget'
import { InvestmentWidget } from './widgets/InvestmentWidget'
import { CarouselWidget } from './widgets/CarouselWidget'
import { GivingGoalWidget } from './widgets/GivingGoalWidget'
import { GiveToCharityWidget } from './widgets/GiveToCharityWidget'
import { HistoryWidget } from './widgets/HistoryWidget'

/**
 * Widget Registry
 *
 * Each widget entry contains:
 * - component: The React component to render
 * - label: Display name in the builder UI
 * - description: Help text for the builder UI
 * - category: Grouping for the widget picker (Advisor, Donor, Common)
 * - defaultProps: Default prop values
 * - propsSchema: Schema for the configuration form
 * - defaultLayout: Default grid layout (columns out of 12)
 */
export const WIDGET_REGISTRY = {
  // ==========================================
  // Advisor Widgets
  // ==========================================
  ClientFundsGrid: {
    component: ClientFundsGridWidget,
    label: 'Client Funds Grid',
    description: 'Displays client fund tiles for advisors with balance info and access buttons',
    category: 'Advisor',
    defaultProps: {},
    propsSchema: {},
    defaultLayout: { columns: 12, order: 2 },
  },

  AdvisorSummary: {
    component: AdvisorSummaryWidget,
    label: 'Advisor Summary',
    description: 'Shows combined totals across all client funds',
    category: 'Advisor',
    fullWidth: true,
    defaultProps: {},
    propsSchema: {},
    defaultLayout: { columns: 12, order: 1 },
  },

  // ==========================================
  // Donor Widgets
  // ==========================================
  FundSummary: {
    component: FundSummaryWidget,
    label: 'Fund Summary',
    description: 'Displays fund balance overview with total, giving wallet, and investment',
    category: 'Donor',
    fullWidth: true,
    defaultProps: {
      showInvestment: true,
    },
    propsSchema: {
      showInvestment: { type: 'boolean', label: 'Show Investment Balance', default: true },
    },
    defaultLayout: { columns: 12, order: 1 },
  },

  ActivityTable: {
    component: ActivityTableWidget,
    label: 'Activity Table',
    description: 'Shows recent transactions (contributions, gifts, transfers)',
    category: 'Donor',
    defaultProps: {
      limit: 10,
      showType: true,
    },
    propsSchema: {
      limit: { type: 'number', label: 'Number of Rows', default: 10, min: 5, max: 50 },
      showType: { type: 'boolean', label: 'Show Transaction Type', default: true },
    },
    defaultLayout: { columns: 12, order: 3 },
  },

  QuickActions: {
    component: QuickActionsWidget,
    label: 'Quick Actions',
    description: 'Action buttons for Contribute, Give, and Share Funds',
    category: 'Donor',
    defaultProps: {
      showContribute: true,
      showGive: true,
      showShare: true,
    },
    propsSchema: {
      showContribute: { type: 'boolean', label: 'Show Contribute Button', default: true },
      showGive: { type: 'boolean', label: 'Show Give Button', default: true },
      showShare: { type: 'boolean', label: 'Show Share Button', default: true },
    },
    defaultLayout: { columns: 12, order: 2 },
  },

  GivingWallet: {
    component: GivingWalletWidget,
    label: 'Giving Wallet',
    description: 'Card showing giving wallet balance',
    category: 'Donor',
    defaultProps: {},
    propsSchema: {},
    defaultLayout: { columns: 6, order: 4 },
  },

  Investment: {
    component: InvestmentWidget,
    label: 'Investment Account',
    description: 'Card showing investment account balance',
    category: 'Donor',
    defaultProps: {},
    propsSchema: {},
    defaultLayout: { columns: 6, order: 5 },
  },

  Carousel: {
    component: CarouselWidget,
    label: 'Carousel',
    description: 'Promotional content carousel with rotating slides',
    category: 'Donor',
    defaultProps: {},
    propsSchema: {},
    defaultLayout: { columns: 12, order: 6 },
  },

  GivingGoal: {
    component: GivingGoalWidget,
    label: 'Giving Goal',
    description: 'Displays giving goal progress and allows setting/updating goals',
    category: 'Donor',
    defaultProps: {
      minHeight: '375px',
    },
    propsSchema: {
      minHeight: { type: 'string', label: 'Minimum Height', default: '375px' },
    },
    defaultLayout: { columns: 6, order: 7 },
  },

  GiveToCharity: {
    component: GiveToCharityWidget,
    label: 'Give To Charity',
    description: 'Charity search with recent and favourite charities',
    category: 'Donor',
    defaultProps: {},
    propsSchema: {},
    defaultLayout: { columns: 6, order: 8 },
  },

  History: {
    component: HistoryWidget,
    label: 'History',
    description: 'Fund statistics showing contributions and gifts history',
    category: 'Donor',
    defaultProps: {},
    propsSchema: {},
    defaultLayout: { columns: 6, order: 9 },
  },
}

/**
 * Get widget by key
 */
export function getWidget(key) {
  return WIDGET_REGISTRY[key] || null
}

/**
 * Get all widgets grouped by category
 */
export function getWidgetsByCategory() {
  const grouped = {}
  Object.entries(WIDGET_REGISTRY).forEach(([key, widget]) => {
    const category = widget.category || 'Other'
    if (!grouped[category]) {
      grouped[category] = []
    }
    grouped[category].push({ key, ...widget })
  })
  return grouped
}

/**
 * Get all widget keys
 */
export function getWidgetKeys() {
  return Object.keys(WIDGET_REGISTRY)
}

/**
 * Get widgets for a specific category
 */
export function getWidgetsForCategory(category) {
  return Object.entries(WIDGET_REGISTRY)
    .filter(([, widget]) => widget.category === category)
    .map(([key, widget]) => ({ key, ...widget }))
}
